/**
 * Pure calculation functions for sales KPIer (KPI #5 + #6, VIB-714).
 * Erik-lock 2026-05-15 — se docs/kpi-source-of-truth.md.
 *
 * Ingen Supabase- eller React-avhengigheter. Hooks wrapper med fetch.
 */

// VIB-1452 terminologi: `bf_*` og BF i interne symboler beholdes, mens den
// brukervendte betegnelsen er GEEK.

// ── KPI #5 Salgs-budsjettoppnåelse leaderboard ─────────────────────────

export interface SalesUnitInput {
  id: string;
  name: string;
  bf_target_mnok: number | null;
}

export interface ProjectOwnerInput {
  sales_unit_id: string | null;
  project_id: string;
  ownership_pct: number | null;
}

export interface ProjectBfInput {
  id: string;
  bf_actual: number | null;
}

export interface SalesLeaderboardRow {
  unitId: string;
  unitName: string;
  /** Årsmål i kroner (bf_target_mnok × 1_000_000) */
  targetKr: number;
  /** Oppnådd BF i kroner (Σ bf_actual × ownership_pct / 100) */
  achievedKr: number;
  /** Prosent av målet. null hvis target = 0 (ingen sammenligningsbase) */
  achievementPct: number | null;
}

/**
 * Beregn leaderboard for salgsenheter for året som sales_units representerer.
 * Sortert descending på achievedKr (best først). Enheter uten oppnådd
 * inkluderes på bunnen.
 *
 * Per Erik-lock i SOT § Salgs-budsjettoppnåelse:
 * - achievement = Σ projects.bf_actual × ownership_pct / 100 for project_owners
 *   hvor sales_unit_id = SU.id.
 * - Bruker bf_actual slik den ligger (lifetime). sales_unit_id er låst ved
 *   prosjektets opprettelsestidspunkt.
 */
export function calcSalesLeaderboard(input: {
  salesUnits: SalesUnitInput[];
  projectOwners: ProjectOwnerInput[];
  projects: ProjectBfInput[];
}): SalesLeaderboardRow[] {
  const { salesUnits, projectOwners, projects } = input;
  const projectMap = new Map(projects.map((p) => [p.id, p]));

  const rows: SalesLeaderboardRow[] = salesUnits.map((su) => {
    const targetKr = Number(su.bf_target_mnok ?? 0) * 1_000_000;

    let achievedKr = 0;
    for (const po of projectOwners) {
      if (po.sales_unit_id !== su.id) continue;
      const project = projectMap.get(po.project_id);
      if (!project) continue;
      const bf = Number(project.bf_actual ?? 0);
      const pct = Number(po.ownership_pct ?? 0);
      achievedKr += bf * (pct / 100);
    }

    const achievementPct = targetKr > 0
      ? Math.round((achievedKr / targetKr) * 1000) / 10
      : null;

    return {
      unitId: su.id,
      unitName: su.name,
      targetKr,
      achievedKr: Math.round(achievedKr),
      achievementPct,
    };
  });

  rows.sort((a, b) => b.achievedKr - a.achievedKr);
  return rows;
}

// ── KPI #6 Selger-drilldown ────────────────────────────────────────────

export interface SellerProjectInput {
  id: string;
  name: string;
  project_code: string | null;
  customer_name: string | null;
  total_amount: number | null;
  bf_diff_pct: number | null;
  total_hours: number;
}

export interface SellerProjectRow {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  customerName: string | null;
  /** Budsjettavvik i prosent (fra projects.bf_diff_pct) */
  budgetDeviationPct: number | null;
  /** Reell timespris: total_amount / total_hours_logged. null hvis ikke beregnbar. */
  reellTimespris: number | null;
}

/**
 * Bygg drilldown-tabell for en selger over deres fullførte prosjekter.
 *
 * Per Erik-lock i SOT § Selger-drilldown:
 * - Caller filtrerer allerede til prosjekter eid av selgeren (via project_owners)
 *   OG project_status='completed'.
 * - Sortering: descending på |budgetDeviationPct| (verste avvik først).
 *   null-verdier havner på bunnen.
 * - reellTimespris = total_amount / total_hours_logged for prosjektet
 *   (lifetime, alle brukere).
 */
export function calcSellerProjectBreakdown(
  projects: SellerProjectInput[],
): SellerProjectRow[] {
  const rows: SellerProjectRow[] = projects.map((p) => {
    const totalAmount = Number(p.total_amount ?? 0);
    const reellTimespris =
      totalAmount > 0 && p.total_hours > 0
        ? Math.round(totalAmount / p.total_hours)
        : null;

    return {
      projectId: p.id,
      projectName: p.name,
      projectCode: p.project_code,
      customerName: p.customer_name,
      budgetDeviationPct: p.bf_diff_pct != null ? Number(p.bf_diff_pct) : null,
      reellTimespris,
    };
  });

  // Sorter descending på |budgetDeviationPct|. null havner på bunnen.
  rows.sort((a, b) => {
    if (a.budgetDeviationPct == null && b.budgetDeviationPct == null) return 0;
    if (a.budgetDeviationPct == null) return 1;
    if (b.budgetDeviationPct == null) return -1;
    return Math.abs(b.budgetDeviationPct) - Math.abs(a.budgetDeviationPct);
  });

  return rows;
}
